import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subject, takeUntil } from 'rxjs';
import * as echarts from 'echarts';
// echarts-gl registra grid3D/surface — sem esse import a série some silenciosamente.
import 'echarts-gl';
import { InjectionMap } from 'src/app/model/response/file-map-response';
import { GridQuantity, displayAxes, loadAxisLabel } from 'src/app/model/injection-map-orientation';
import { mapAxisText, mapCellText } from 'src/app/model/injection-map-format';

const JET = ['#000080', '#0000ff', '#00ffff', '#00ff00', '#ffff00', '#ff7f00', '#ff0000', '#7f0000'];

/**
 * Surface 3D do mapa de injeção, em eixos `value` (coordenada real, não índice).
 *
 * A grandeza de cada eixo segue `inverted_axis` via `displayAxes()` — a mesma regra do
 * heatmap 2D, para que alternar 2D↔3D não troque os eixos de lugar. Aqui a troca é feita
 * escolhendo QUAL valor vai em cada coordenada; nunca permutando `values_ms`, que
 * desamarraria o ms do seu par (rpm, load).
 *
 * Botão esquerdo = girar, botão direito = pan
 * (contextmenu nativo bloqueado pra liberar o botão direito).
 */
@Component({
  selector: 'app-injection-map-3d',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '<div #chart class="chart-3d" (contextmenu)="$event.preventDefault()"></div>',
  styles: [`
    :host, .chart-3d { display: block; width: 100%; height: 100%; }
  `],
})
export class InjectionMap3dComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() map!: InjectionMap;
  @ViewChild('chart', { static: true }) chartEl!: ElementRef<HTMLDivElement>;

  private instance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly destroy$ = new Subject<void>();
  /** Nome traduzido do eixo Z; atualizado quando o idioma muda. */
  private zAxisName = '';

  constructor(private translocoService: TranslocoService) {}

  ngAfterViewInit(): void {
    this.instance = echarts.init(this.chartEl.nativeElement, 'dark');
    this.zAxisName = this.translocoService.translate('injectionMap.injectionTimeMs');
    this.render();
    // Depois do 1º render: o setOption em merge não pode cair num gráfico vazio.
    this.translocoService.selectTranslate('injectionMap.injectionTimeMs')
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        this.zAxisName = name;
        // Só o nome, em merge: não reseta a câmera nem a superfície.
        this.instance?.setOption({ zAxis3D: { name } });
      });
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.instance?.resize());
      this.resizeObserver.observe(this.chartEl.nativeElement);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['map'] && this.instance) this.render();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.instance?.dispose();
    this.instance = null;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.instance?.resize();
  }

  resize(): void {
    this.instance?.resize();
  }

  /**
   * Posiciona um ponto vermelho sobre a superfície na coordenada (load, rpm),
   * com z = bilinear(load, rpm) no values_ms. Merge parcial.
   */
  setSite(loadValue: number, rpmValue: number): void {
    if (!this.instance || !this.map) return;
    const { rpm, load, values } = this.normalize(this.map);
    const rpmFrac  = this.fracIndex(rpmValue,  rpm);
    const loadFrac = this.fracIndex(loadValue, load);
    if (rpmFrac === null || loadFrac === null) {
      this.clearSite();
      return;
    }
    // `bilinear` indexa o ARMAZENAMENTO (values[rpm][load]), então não muda com a troca de
    // eixo. O que precisa acompanhar é a coordenada do marker — se ela ficasse fixa em
    // (rpm, load) enquanto a superfície troca de eixo, o ponto vermelho apontaria errado.
    const z = this.bilinear(rpmFrac, loadFrac, values);
    if (z === null) {
      // Vizinho ausente: sem ponto confiável, nunca inventar 0 sobre a superfície.
      this.clearSite();
      return;
    }
    const { xIs, yIs } = displayAxes(this.map);
    const site: [number, number, number] = [
      xIs === 'rpm' ? rpmValue : loadValue,
      yIs === 'rpm' ? rpmValue : loadValue,
      z,
    ];
    // Endereça a série por id pra atualizar SÓ o marker (não toca na superfície).
    this.instance.setOption({
      series: [{ id: 'site3d', data: [site] }],
    });
  }

  clearSite(): void {
    if (!this.instance) return;
    this.instance.setOption({
      series: [{ id: 'site3d', data: [] }],
    });
  }

  private fracIndex(value: number, arr: number[]): number | null {
    if (arr.length === 0) return null;
    const min = Math.min(arr[0], arr[arr.length - 1]);
    const max = Math.max(arr[0], arr[arr.length - 1]);
    if (value < min || value > max) return null;
    for (let i = 0; i < arr.length - 1; i++) {
      const a = arr[i];
      const b = arr[i + 1];
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      if (value >= lo && value <= hi) {
        const t = (b === a) ? 0 : (value - a) / (b - a);
        return i + t;
      }
    }
    return arr.length - 1;
  }

  /** Interpola a célula; `null` se algum dos 4 vizinhos faltar ou não for finito. */
  private bilinear(rpmFrac: number, loadFrac: number, values: number[][]): number | null {
    const i0 = Math.floor(rpmFrac);
    const i1 = Math.min(i0 + 1, values.length - 1);
    const j0 = Math.floor(loadFrac);
    const j1 = Math.min(j0 + 1, values[i0].length - 1);
    const di = rpmFrac - i0;
    const dj = loadFrac - j0;
    const v00 = values[i0][j0];
    const v01 = values[i0][j1];
    const v10 = values[i1][j0];
    const v11 = values[i1][j1];
    if (![v00, v01, v10, v11].every((v) => Number.isFinite(v))) return null;
    return v00 * (1 - di) * (1 - dj)
         + v01 * (1 - di) * dj
         + v10 * di       * (1 - dj)
         + v11 * di       * dj;
  }

  private render(): void {
    if (!this.map || !this.instance) return;

    const { rpm, load, values } = this.normalize(this.map);
    const { xIs, yIs } = displayAxes(this.map);

    // A superfície do echarts-gl lê a lista como uma grade percorrida linha a linha, com
    // X variando no laço INTERNO (ela deduz "nova linha" quando X diminui). Se X for o RPM
    // e o interno variar a carga, X nunca diminui, a dedução falha e a malha sai serrilhada.
    // Por isso o eixo que vai em X é sempre o interno; `values[i][j]` continua sendo
    // (rpm[i], load[j]), só muda a ordem de visita. `dataShape` ainda vai explícito na
    // série, para não depender da dedução.
    const xAxisValues = xIs === 'rpm' ? rpm : load;
    const yAxisValues = yIs === 'rpm' ? rpm : load;
    const data: Array<[number, number, number]> = [];
    let min = Infinity;
    let max = -Infinity;
    for (let yi = 0; yi < yAxisValues.length; yi++) {
      for (let xi = 0; xi < xAxisValues.length; xi++) {
        const i = xIs === 'rpm' ? xi : yi; // índice de RPM (linha de values)
        const j = xIs === 'rpm' ? yi : xi; // índice de carga (coluna de values)
        const v = values[i][j];
        data.push([xAxisValues[xi], yAxisValues[yi], v]);
        // Célula ausente (não finita) fica na série, mas não entra na escala de cor (D8).
        if (Number.isFinite(v)) {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    const dataShape: [number, number] = [yAxisValues.length, xAxisValues.length];
    if (!Number.isFinite(min) || !Number.isFinite(max)) { min = 0; max = 1; }
    if (min === max) max = min + 1;

    this.instance.setOption(
      {
        backgroundColor: 'transparent',
        tooltip: {
          // Rotula pela grandeza real de cada eixo (não assume RPM no X).
          formatter: (p: any) =>
            [
              `<b>${this.quantityLabel(xIs)}:</b> ${this.fmtAxis(xIs, p.value[0])}`,
              `<b>${this.quantityLabel(yIs)}:</b> ${this.fmtAxis(yIs, p.value[1])}`,
              `<b>${this.translocoService.translate('injectionMap.injectionTime')}:</b> ${this.fmt(p.value[2])} ${this.escapeUnit(this.map.units)}`,
            ].join('<br/>'),
        },
        visualMap: {
          min, max,
          calculable: true,
          orient: 'vertical',
          right: 4,
          top: 'middle',
          itemWidth: 10,
          itemHeight: 120,
          inRange: { color: JET },
          textStyle: { color: '#9b9b9b', fontSize: 10 },
          // Rótulos dos extremos cortados em 3 casas (D1), não o arredondamento do ECharts.
          formatter: (v: number) => mapCellText(Number(v)),
          dimension: 2,
          seriesIndex: 0,
        },
        xAxis3D: { type: 'value', name: this.quantityLabel(xIs), axisLabel: { color: '#9b9b9b', formatter: (v: number) => mapAxisText(Number(v)) } },
        yAxis3D: { type: 'value', name: this.quantityLabel(yIs), axisLabel: { color: '#9b9b9b', formatter: (v: number) => mapAxisText(Number(v)) } },
        zAxis3D: { type: 'value', name: this.zAxisName, axisLabel: { color: '#9b9b9b', formatter: (v: number) => mapCellText(Number(v)) } },
        grid3D: {
          boxWidth: 110,
          boxDepth: 90,
          boxHeight: 60,
          viewControl: {
            distance: 220,
            alpha: 35,
            beta: 35,
            panMouseButton: 'right',
            rotateMouseButton: 'left',
          },
          light: {
            main: { intensity: 1.2, shadow: true, alpha: 30, beta: 40 },
            ambient: { intensity: 0.4 },
          },
          temporalSuperSampling: { enable: false },
          postEffect: { enable: false },
        },
        series: [
          {
            type: 'surface',
            data,
            dataShape,
            wireframe: { show: true, lineStyle: { color: 'rgba(255,255,255,0.25)', width: 1 } },
            shading: 'realistic',
            realisticMaterial: { roughness: 0.5, metalness: 0 },
          },
          // Site cursor sincronizado com o hover do log
          {
            type: 'scatter3D',
            id: 'site3d',
            data: [],
            symbolSize: 14,
            itemStyle: { color: '#ff0000', opacity: 1 },
            silent: true,
            animation: false,
          },
        ],
      },
      true,
    );
  }

  /**
   * Ordena `rpm`/`load` em ordem ascendente movendo `values_ms` junto — transformação
   * NEUTRA (nenhum valor muda de par de coordenadas), necessária porque `interpolate()`
   * indexa por fração assumindo eixos monotônicos crescentes.
   *
   * Os flags `inverted_axis`/`inverted_rows`/`inverted_cols` NÃO são aplicados aqui, de
   * propósito: são orientação de EXIBIÇÃO de grade (contrato §3.2) e a superfície 3D usa
   * eixos de VALOR — cada ponto vai em `(rpm[i], load[j])`, coordenada real. Permutar a
   * matriz aqui não giraria a vista, apenas atribuiria o ms errado à coordenada (era o
   * bug anterior). Quem aplica os flags é o heatmap 2D, que é grade categórica.
   */
  private normalize(src: InjectionMap) {
    const rpm = [...src.rpm];
    const load = [...src.load];
    let values = src.values_ms.map((row) => [...row]);

    if (rpm.length >= 2 && rpm[0] > rpm[rpm.length - 1]) {
      rpm.reverse();
      values.reverse(); // linhas = rpm: eixo e matriz juntos
    }
    if (load.length >= 2 && load[0] > load[load.length - 1]) {
      load.reverse();
      values = values.map((row) => row.reverse()); // colunas = load: eixo e matriz juntos
    }

    return { rpm, load, values };
  }

  /** Nome da grandeza de um eixo: RPM, ou o `axis` do mapa (MAP/TPS) para a carga. */
  private quantityLabel(q: GridQuantity): string {
    return q === 'rpm' ? 'RPM' : loadAxisLabel(this.map.axis);
  }

  /** Eixo: até 3 casas cortadas, sem zeros forçados (igual às tabelas de ajuste). */
  private fmtAxis(_q: GridQuantity, v: number): string {
    return mapAxisText(Number(v));
  }

  private escapeUnit(s: string): string {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Tempo de injeção em ms, com 3 casas cortadas (igual aos editores). */
  private fmt(v: number): string {
    return mapCellText(v);
  }
}
